import useIsMobile from "@app/hooks/useIsMobile";
import {
  ButtonProps,
  IconButton,
  Link,
  LinkProps,
  SxProps,
  Typography,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import React, { useEffect, useState } from "react";

export interface IRauliButtonLink extends LinkProps {
  forwardedRef?: React.ForwardedRef<HTMLAnchorElement>;
  text: string | React.ReactElement;
  mobileText: string | React.ReactElement;
  title: string;
  icon: React.ReactElement;
  reverse?: boolean;
  disabled?: boolean;
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  capitalized?: boolean;
  buttonProps?: ButtonProps;
  sx?: SxProps;
  inProgress?: boolean;
  shakeOnActive?: boolean;
  disableShakeOnClick?: boolean;
}

const RauliButtonLink = ({
  forwardedRef,
  text,
  mobileText,
  icon,
  title,
  reverse = false,
  disabled = false,
  capitalized = false,
  buttonProps = {},
  sx = {},
  inProgress = false,
  shakeOnActive = false,
  disableShakeOnClick = true,
  ...props
}: IRauliButtonLink) => {
  const isMobile = useIsMobile();
  const { sx: btnSx, ...btnProps } = buttonProps;
  const [shouldShake, setShouldShake] = useState(false);
  const [prevDisabled, setPrevDisabled] = useState(disabled);

  // Detect when disabled state changes from true to false
  useEffect(() => {
    if (prevDisabled && !disabled && shakeOnActive) {
      setShouldShake(true);
    } else if (disabled) {
      // Stop shaking when button becomes disabled
      setShouldShake(false);
    }
    setPrevDisabled(disabled);
  }, [disabled, prevDisabled, shakeOnActive]);

  return (
    <Link
      ref={forwardedRef}
      className={disabled || inProgress ? "disabled" : undefined}
      href="#"
      {...props}
      title={title}
      aria-label={title}
      onClick={(event: React.MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();
        if (disableShakeOnClick) {
          setShouldShake(false);
        }
        if (disabled) {
          return;
        }
        props.onClick && props.onClick(event);
      }}
      sx={{
        display: "flex",
        flexDirection: reverse ? "row-reverse" : "row",
        alignItems: "center",
        lineHeight: 1,
        paddingTop: 0,
        ...sx,
      }}
    >
      {isMobile ? (
        <Typography
          variant={capitalized ? "button" : "body1"}
          sx={{ lineHeight: 1, textAlign: "right" }}
        >
          {mobileText}
        </Typography>
      ) : text ? (
        <Typography
          variant={capitalized ? "button" : "body1"}
          sx={{ lineHeight: 1, textAlign: "right" }}
        >
          {text}
        </Typography>
      ) : (
        text && (
          <Typography variant={capitalized ? "button" : "body1"}>
            {text}
          </Typography>
        )
      )}
      <IconButton
        size="small"
        title={title}
        aria-label={title}
        disabled={disabled || inProgress}
        sx={{
          width: isMobile ? 28 : 35,
          height: isMobile ? 28 : 35,
          borderRadius: 1,
          marginLeft: reverse ? 0 : 1,
          marginRight: reverse ? 1 : 0,
          "& .MuiSvgIcon-root": {
            height: isMobile ? 20 : 24,
            width: isMobile ? 20 : 24,
          },
          ...(shouldShake &&
            !inProgress && {
              animation: "shake 2s ease-in-out infinite",
              "@keyframes shake": {
                "0%, 33.33%": { transform: "translateX(0)" },
                "3.33%, 13.33%, 23.33%": { transform: "translateX(-3px)" },
                "6.67%, 16.67%, 26.67%": { transform: "translateX(3px)" },
              },
            }),
          ...btnSx,
        }}
        {...btnProps}
      >
        {inProgress ? (
          <CircularProgress size={isMobile ? 16 : 20} color="inherit" />
        ) : (
          icon
        )}
      </IconButton>
    </Link>
  );
};

export default React.forwardRef(
  (props: IRauliButtonLink, ref: React.ForwardedRef<HTMLAnchorElement>) => (
    <RauliButtonLink {...props} forwardedRef={ref} />
  ),
);
